import { useEffect, useState } from 'react';
import { Loader2, Check, X, Building2, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/adminApi';

const TABS = [
  { id: 'businesses', label: 'Businesses', icon: Building2 },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'communities', label: 'Communities', icon: Users },
];

export default function AdminSubmissions() {
  const [tab, setTab] = useState('businesses');
  const [data, setData] = useState({ businesses: [], events: [], communities: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi(`/admin/submissions?status=${filter}`);
      setData({
        businesses: res.business_submissions || [],
        events: res.event_submissions || [],
        communities: res.community_submissions || [],
      });
    } catch (err) {
      toast.error(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function decide(kind, id, action, reason = '') {
    try {
      const path = `/admin/submissions/${kind}/${id}/${action}`;
      await adminApi(path, { method: 'POST', body: JSON.stringify(action === 'reject' ? { reason: reason || 'Does not meet quality guidelines' } : {}) });
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'}`);
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  const items = data[tab] || [];

  return (
    <div className="p-8" data-testid="admin-submissions-page">
      <h1 className="text-3xl font-serif font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Submissions</h1>
      <p className="text-white/50 text-sm mb-5">Review and approve community-submitted listings.</p>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 bg-[#1A1820] border border-white/10 rounded-full p-1">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-submissions-tab-${id}`} className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${tab === id ? 'bg-[#C8553D] text-white' : 'text-white/60'}`}>
              <Icon className="w-3.5 h-3.5" /> {label} ({data[id]?.length || 0})
            </button>
          ))}
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-submissions-filter" className="bg-[#1A1820] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-[#1A1820] border border-white/10 rounded-2xl text-white/50">No submissions</div>
      ) : (
        <ul className="space-y-3" data-testid="admin-submissions-list">
          {items.map((s) => {
            const title = s.title || s.name;
            const img = s.images?.[0] || s.image_url;
            const kind = tab.slice(0, -1); // businesses → business
            return (
              <li key={s.id} data-testid={`admin-submission-${s.id}`} className="bg-[#1A1820] border border-white/10 rounded-2xl p-4 flex gap-4">
                <div className="w-20 h-20 rounded-lg bg-white/5 overflow-hidden flex-shrink-0">
                  {img ? <img src={img} alt={title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20">—</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <h3 className="font-bold text-white truncate">{title}</h3>
                    <StatusPill status={s.status} />
                  </div>
                  <p className="text-xs text-white/50">{s.category} · {s.city}{s.venue ? ` · ${s.venue}` : ''}</p>
                  <p className="text-sm text-white/70 mt-2 line-clamp-2">{s.description}</p>
                  <p className="text-[10px] text-white/40 mt-2">Submitted by {s.submitter_name || 'user'} · {new Date(s.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</p>
                  {s.status === 'pending' && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => decide(kind, s.id, 'approve')} data-testid={`admin-submission-approve-${s.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-[#2A9D8F]/20 hover:bg-[#2A9D8F]/40 text-[#2A9D8F] font-bold rounded text-xs">
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button onClick={() => { const r = window.prompt('Reason (optional):', 'Does not meet quality guidelines'); if (r !== null) decide(kind, s.id, 'reject', r); }} data-testid={`admin-submission-reject-${s.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-[#E76F51]/20 hover:bg-[#E76F51]/40 text-[#E76F51] font-bold rounded text-xs">
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: ['#E9C46A', 'PENDING'],
    approved: ['#2A9D8F', 'APPROVED'],
    rejected: ['#E76F51', 'REJECTED'],
  };
  const [c, l] = map[status] || ['#fff', status];
  return <span className="px-2 py-0.5 rounded text-[9px] font-bold tracking-wider" style={{ backgroundColor: c + '30', color: c }}>{l}</span>;
}
